/**
 * useWizardStepDraft — shared hook for wizard step draft persistence.
 *
 * Why this hook exists
 * --------------------
 * Field engineers in the setup wizard rarely complete every page in one
 * sitting. They get pulled to another job, the laptop sleeps, the browser
 * crashes, or they intentionally close the tab to come back tomorrow. Until
 * recently only StepLiftsFloors and StepChannelModifiers persisted their
 * in-progress form state to the server-side wizard_drafts table — every
 * other step kept its form state in React-local useState and lost it on
 * reload. The operator would see a blank form and have to retype their
 * configuration from scratch.
 *
 * This hook centralizes the "load draft on mount, save draft on change"
 * dance so every wizard step can opt in by passing the few fields the
 * operator actually decides:
 *
 *   useWizardStepDraft({
 *     orgId: wizardState.organizationId,
 *     step: WizardStep.Bacnet,
 *     scopeId: "bacnet",
 *     scopeType: "step",
 *     draft: { deviceId, objectName },              // ← what to save
 *     onRestore: (saved) => {                       // ← how to restore
 *       if (typeof saved.deviceId === "number") setDeviceId(saved.deviceId);
 *       ...
 *     },
 *     validate: (snapshot) => typeof snapshot.deviceId === "number",
 *   });
 *
 * Wire-format notes
 * -----------------
 * Drafts ride the same `createFlatBufferBaseQuery` transport every other
 * wizard endpoint uses — see `wizardDraftsAPI.ts`. The `draftData` payload
 * is round-tripped as a JS object on the wire envelope but the *transport*
 * (encoding, retry middleware, error envelope) is the unified FlatBuffer
 * pipeline. Steps must NEVER call `fetch()` directly or open a parallel
 * JSON path for drafts; this hook is the single entry point and goes
 * through the registered API so no JSON-only code path can creep in.
 *
 * `scopeId`/`scopeType` above are the human-readable values this hook's
 * callers use. LiftCloud requires a real UUID for `scope_id` and one of
 * bank/server/org for `scope_type`, so `wizardDraftsAPI.ts` translates
 * slugs and `"step"` at the wire boundary (see `wizardDraftScope.ts`) —
 * callers never need to know that.
 *
 * Why the hook serializes internally
 * ----------------------------------
 * Callers naturally pass `{ deviceId, objectName }` inline — a fresh
 * object reference every render. If the save effect simply depended on
 * `draft` by identity, every parent re-render would restart the
 * debounce timer and starve out the 750 ms window. Worse, the operator
 * could pause typing for ~700 ms only for an unrelated render to clear
 * the timer.
 *
 * To dodge both pitfalls the hook serializes the draft to a stable
 * string and depends on that hash. React then re-runs the save effect
 * exactly when the *content* changes, not when the *reference* changes,
 * and the inline object pattern stays ergonomic at the call site.
 * Drafts are small (operator-typed form values), so the per-render
 * stringify cost is negligible.
 *
 * Behavioral contract
 * -------------------
 *   1. On mount (and whenever `orgId` becomes non-null), fire one GET.
 *      If a draft exists, hand it to `onRestore` so the caller can
 *      hydrate its own useState. After a successful restore (or a
 *      benign 404) flip `draftRestoredRef` so step 2 is allowed to run.
 *   2. After restore, watch the serialized draft. On any content
 *      change, schedule a debounced (750 ms) PUT. A burst of keystrokes
 *      therefore produces ONE network call, not many.
 *   3. If `validate(snapshot)` is supplied and returns false, the PUT
 *      is skipped — useful when the operator is mid-edit and persisting
 *      would clobber a previously-valid saved state. Drafts that pass
 *      validation are saved freely.
 *   4. When the step unmounts (navigation to another wizard step,
 *      route change, tab close in a way the runtime can observe), a
 *      final flush fires *immediately* with the latest snapshot —
 *      ignoring the remaining debounce window. This is the "verify
 *      and save before moving on" guarantee the wizard relies on so
 *      the last few keystrokes before "Next" never disappear.
 *
 * Verification semantics
 * ----------------------
 * "Verification" in this hook means two distinct checks:
 *   - **save-side verification:** the optional `validate` callback gates
 *     whether the current snapshot is worth saving. Don't use it to
 *     enforce "the step is complete" semantics — drafts are deliberately
 *     allowed to be partial. Use it only to filter out unrecoverably
 *     malformed snapshots (e.g., transiently undefined enum values).
 *   - **restore-side verification:** the `onRestore` callback should
 *     defensively type-check each restored field (`typeof x === "string"`
 *     etc.) before writing it into state. The persisted draft is
 *     operator-controlled data and must not be trusted to match the
 *     current TS shape if schemas have evolved.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import GETJwtToken from "../../../redux/authentication/authentication";
import {
  useLazyGetDraftQuery,
  useUpsertDraftMutation,
} from "../../../redux/wizardDrafts/wizardDraftsAPI";

export interface UseWizardStepDraftOptions<TDraft extends object> {
  /** Wizard organization the draft is scoped to. Hook is a no-op until non-null. */
  orgId: string | null;
  /** Wizard step index, see `WizardStep` in wizardInvalidation.ts. */
  step: number;
  /** Unique scope key within the step. Use the step's slug (e.g. "bacnet")
   *  for single-instance steps and the entity id (e.g. a bankId) for
   *  per-entity drafts like StepLiftsFloors. */
  scopeId: string;
  /** Logical scope category. Use `"step"` for whole-step drafts and the
   *  entity type (`"bank"`, etc.) for per-entity drafts. Persisted with
   *  the draft so the cleanup path can target whole categories at once. */
  scopeType: string;
  /** Current snapshot of every field worth persisting. Re-evaluated on
   *  every render — that's fine, the hook compares by serialized content
   *  so identity changes alone don't reschedule the timer. */
  draft: TDraft;
  /** Apply a previously-saved draft to local state. Called exactly once
   *  per mount once the GET resolves (with the saved snapshot) or never
   *  if no draft exists. */
  onRestore: (saved: Partial<TDraft>) => void;
  /** Optional save-side validator. Return false to skip the upsert for
   *  this snapshot — the next change re-triggers the debounce. */
  validate?: (snapshot: TDraft) => boolean;
  /** Disable the hook entirely without changing call shape. Useful for
   *  unit tests or for steps that conditionally render the draft logic
   *  (e.g., behind a feature flag). */
  disabled?: boolean;
}

const DEBOUNCE_MS = 750;

/**
 * Stable, content-addressed key for a draft snapshot. We hash via
 * JSON.stringify because the snapshots are small operator-typed forms
 * and JSON ordering is deterministic for the shapes we accept (plain
 * objects, arrays, primitives, no functions/symbols). If serialization
 * fails (cycles, BigInt, etc.) return a sentinel so the save effect
 * fires unconditionally — better to over-save than to silently swallow
 * the change.
 */
function hashDraft(draft: object): string {
  try {
    return JSON.stringify(draft);
  } catch {
    return `__unserializable_${Date.now()}`;
  }
}

export function useWizardStepDraft<TDraft extends object>(
  options: UseWizardStepDraftOptions<TDraft>,
): { isRestored: boolean } {
  const { orgId, step, scopeId, scopeType, draft, onRestore, validate, disabled } = options;

  const [triggerGetDraft] = useLazyGetDraftQuery();
  const [triggerUpsertDraft] = useUpsertDraftMutation();

  /** Synchronous re-entry guard. `useRef` over `useState` because the
   *  guard is read inside the save effect's setTimeout closure and a
   *  React state update would lag a render behind. */
  const draftRestoredRef = useRef(false);
  // State mirror of draftRestoredRef so callers can react to restore
  // completion (the ref alone is invisible to effects/renders). Flips true
  // exactly once — after a successful restore OR a benign 404/failure — so a
  // consumer that must not act until this draft's data is loaded (e.g. the
  // Actions auto-heal, which must know the operator's deleted-command memory
  // before re-creating anything) can gate on it.
  const [isRestored, setIsRestored] = useState(false);

  // ───────────────────────────────────────────────────────────────────
  // Latest-callback / latest-draft refs.
  //
  // Callers commonly pass an inline arrow for `onRestore` / `validate`
  // whose identity changes every render. The save effect uses these
  // through refs so a new closure reference doesn't refire the effect.
  // The draft itself goes through a ref too because the effect depends
  // on the *hash*, not the object — we still need the live object
  // available inside the setTimeout callback.
  // ───────────────────────────────────────────────────────────────────
  const draftRef = useRef(draft);
  const onRestoreRef = useRef(onRestore);
  const validateRef = useRef(validate);
  draftRef.current = draft;
  onRestoreRef.current = onRestore;
  validateRef.current = validate;

  /** Content-addressed key of the current draft. useMemo so the hash is
   *  computed once per render even though it's referenced by both the
   *  current effect deps and any potential downstream consumer of this
   *  hook in the future. */
  const draftHash = useMemo(() => hashDraft(draft), [draft]);

  // ───────────────────────────────────────────────────────────────────
  // Restore on mount (one-shot per orgId+scope).
  //
  // `draftRestoredRef` guards re-entry, so even when one of the effect
  // dependencies changes after the first run the body short-circuits
  // immediately — exactly one GET per scope per mount.
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (disabled || !orgId || draftRestoredRef.current) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const token = await GETJwtToken();
        const result = await triggerGetDraft({ orgId, step, scopeId, token }).unwrap();
        if (cancelled) return;
        const saved = result?.data?.draft_data;
        if (saved && typeof saved === "object") {
          onRestoreRef.current(saved as Partial<TDraft>);
        }
        draftRestoredRef.current = true;
        setIsRestored(true);
      } catch {
        // 404 (no draft yet) or transient failure. Flag as restored so
        // the auto-save effect can begin — the operator's first edit
        // will create a fresh draft row server-side.
        draftRestoredRef.current = true;
        if (!cancelled) setIsRestored(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, step, scopeId, disabled, triggerGetDraft]);

  // ───────────────────────────────────────────────────────────────────
  // Debounced auto-save after restore.
  //
  // The effect depends on `draftHash` (a primitive string) so it only
  // reruns when the draft's CONTENT changes — not on every parent
  // re-render. Identity-based comparison on the raw draft object would
  // refire on every render and starve the timer.
  //
  // Tracking the last saved hash lets the unmount-flush effect tell
  // whether the latest changes have already been persisted.
  // ───────────────────────────────────────────────────────────────────
  const lastSavedHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (disabled || !orgId || !draftRestoredRef.current) return undefined;
    const validator = validateRef.current;
    if (validator && !validator(draftRef.current)) return undefined;

    const timer = setTimeout(async () => {
      try {
        const token = await GETJwtToken();
        triggerUpsertDraft({
          orgId,
          step,
          scopeId,
          scopeType,
          draftData: draftRef.current,
          token,
        });
        lastSavedHashRef.current = draftHash;
      } catch {
        // Transient save failures are non-fatal — the work is still in
        // React state and the next edit will retry automatically.
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [orgId, step, scopeId, scopeType, draftHash, disabled, triggerUpsertDraft]);

  // ───────────────────────────────────────────────────────────────────
  // Final-flush on unmount.
  //
  // The debounce above clears its timer in the cleanup. When the
  // cleanup is "the deps changed and we're about to schedule a new
  // timer," that's fine — the next 750 ms window will pick up the
  // change. When the cleanup is "the component unmounted," the timer
  // is cleared and never replaced. Without this second effect the
  // operator could type their last keystroke at t=740 ms and hit
  // "Next" at t=745 ms — the timer would be canceled with the value
  // unsaved, defeating the whole point of draft persistence.
  //
  // This effect's cleanup runs exactly once at unmount because the
  // empty dep array means the body never re-runs after mount. Inside
  // the cleanup we check whether the latest snapshot has been saved
  // (`lastSavedHashRef`) and fire a synchronous fire-and-forget
  // upsert if not. We deliberately don't await the upsert — by the
  // time React has called the cleanup the component is gone, and
  // RTK Query's middleware handles the pending request independently.
  // ───────────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      if (disabled || !orgId || !draftRestoredRef.current) return;
      const snapshot = draftRef.current;
      const validator = validateRef.current;
      if (validator && !validator(snapshot)) return;
      let finalHash: string | null;
      try {
        finalHash = JSON.stringify(snapshot);
      } catch {
        finalHash = null;
      }
      if (finalHash !== null && finalHash === lastSavedHashRef.current) return;
      const fireAndForget = async () => {
        try {
          const token = await GETJwtToken();
          triggerUpsertDraft({
            orgId,
            step,
            scopeId,
            scopeType,
            draftData: snapshot,
            token,
          });
        } catch {
          // Best-effort — the component is gone, so retry would be
          // a tree-falls-in-a-forest situation. Operators get one
          // shot at this final save; failures here are rare in
          // practice and the auto-save covered any earlier window.
        }
      };
      // The promise is intentionally not awaited; the runtime's
      // unhandled-rejection guard catches anything fireAndForget didn't.
      fireAndForget().catch(() => undefined);
    },
    [orgId, step, scopeId, scopeType, disabled, triggerUpsertDraft],
  );

  return { isRestored };
}
